import type { KbCategory, KbDocDTO, KbStatsDTO } from '@copilot/shared';
import { KbCategorySchema } from '@copilot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { kbApi } from '../../api/admin';
import {
  Badge,
  Button,
  ButtonLink,
  Card,
  Checkbox,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  PageSkeleton,
  Meter,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
  Tr,
} from '../../components';
import { formatDate, formatNumber } from '../../lib/format';
import { KB_CATEGORY_LABELS } from '../../lib/labels';

export function KbPage() {
  const [sp, setSp] = useSearchParams();
  const category = KbCategorySchema.options.find((c) => c === sp.get('category'));
  const docs = useQuery({ queryKey: ['kb', 'docs'], queryFn: kbApi.list });
  const [uploading, setUploading] = useState(false);

  const groups = KbCategorySchema.options
    .filter((c) => !category || c === category)
    .map((c) => ({ category: c, docs: (docs.data ?? []).filter((d) => d.category === c).sort((a, b) => a.title.localeCompare(b.title)) }))
    .filter((g) => g.docs.length > 0);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Knowledge base"
        description="Company material the AI uses for live tips and answers."
        actions={
          <>
            <Button onClick={() => setUploading(true)}>Upload file</Button>
            <ButtonLink to="/kb/new" variant="primary">
              New document
            </ButtonLink>
          </>
        }
      />
      <StatsBanner />
      <div className="flex items-center gap-2">
        <label htmlFor="kb-category" className="text-xs text-muted">
          Category
        </label>
        <Select
          id="kb-category"
          className="w-56"
          value={category ?? ''}
          onChange={(e) => setSp(e.target.value ? { category: e.target.value } : {}, { replace: true })}
        >
          <option value="">All categories</option>
          {KbCategorySchema.options.map((c) => (
            <option key={c} value={c}>
              {KB_CATEGORY_LABELS[c]}
            </option>
          ))}
        </Select>
      </div>

      {docs.isError && <ErrorBanner error={docs.error} onRetry={() => void docs.refetch()} />}
      {docs.isLoading && <PageSkeleton />}
      {docs.isSuccess && groups.length === 0 && (
        <Card>
          <EmptyState title={category ? 'No documents in this category' : 'The knowledge base is empty'}>
            Add service descriptions, case studies, pricing guidance, competitor notes and objection handling so tips are specific
            to Clustox.
          </EmptyState>
        </Card>
      )}
      {groups.map((g) => (
        <Card key={g.category} title={KB_CATEGORY_LABELS[g.category]} bodyClassName="p-0">
          <Table>
            <thead>
              <tr>
                <Th>Title</Th>
                <Th className="text-right">Version</Th>
                <Th className="text-right">Tokens</Th>
                <Th>Updated</Th>
                <Th className="w-24">Active</Th>
              </tr>
            </thead>
            <tbody>
              {g.docs.map((d) => (
                <DocRow key={d.id} doc={d} />
              ))}
            </tbody>
          </Table>
        </Card>
      ))}
      <UploadModal open={uploading} onClose={() => setUploading(false)} />
    </div>
  );
}

function DocRow({ doc }: { doc: KbDocDTO }) {
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: (active: boolean) => kbApi.patch(doc.id, { active }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['kb'] }),
  });
  return (
    <Tr className={doc.active ? undefined : 'opacity-60'}>
      <Td>
        <Link to={`/kb/${doc.id}`} className="font-medium text-ink hover:text-accent-ink hover:underline">
          {doc.title}
        </Link>
        {doc.sourceFilename && <div className="text-xs text-muted">{doc.sourceFilename}</div>}
        {toggle.isError && <div className="text-xs text-danger">Could not update: {toggle.error.message}</div>}
      </Td>
      <Td className="text-right tabular-nums">v{doc.version}</Td>
      <Td className="text-right tabular-nums">{formatNumber(doc.tokens)}</Td>
      <Td className="whitespace-nowrap text-ink-2">
        {formatDate(doc.updatedAt)}
        {doc.updatedByName && <div className="text-xs text-muted">{doc.updatedByName}</div>}
      </Td>
      <Td>
        <span className="inline-flex items-center gap-2">
          <Checkbox
            aria-label={`${doc.title} active`}
            checked={toggle.isPending ? !!toggle.variables : doc.active}
            disabled={toggle.isPending}
            onChange={(e) => toggle.mutate(e.target.checked)}
            label={doc.active ? 'On' : 'Off'}
          />
          {toggle.isPending && <Spinner size="sm" />}
        </span>
      </Td>
    </Tr>
  );
}

export function StatsBanner() {
  const stats = useQuery({ queryKey: ['kb', 'stats'], queryFn: kbApi.stats });
  if (stats.isLoading) return null;
  if (stats.isError) return <ErrorBanner error={stats.error} onRetry={() => void stats.refetch()} />;
  const s = stats.data as KbStatsDTO;
  const full = s.mode === 'full';
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink">
            {formatNumber(s.totalTokens)} of {formatNumber(s.retrievalThresholdTokens)} tokens in {formatNumber(s.activeDocs)} active
            documents
          </span>
          <Badge tone={full ? 'good' : 'warn'}>{full ? 'Full context mode' : 'Retrieval mode'}</Badge>
        </div>
      </div>
      <Meter value={s.totalTokens} max={s.retrievalThresholdTokens} softPct={90} className="mt-2" />
      <p className="mt-2 text-xs text-muted">
        {full
          ? 'All active documents are sent to the AI with every request (prompt-cached, so repeated calls are cheap). Once active content grows past the threshold, the server switches to retrieval mode.'
          : 'Active content exceeds the threshold, so only the chunks most relevant to the current conversation are sent to the AI. Deactivate documents that are rarely useful to go back to full context mode.'}
      </p>
    </div>
  );
}

const ACCEPT = '.md,.txt,.pdf,.docx';

function UploadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<KbCategory>('service');
  const [validation, setValidation] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: () => kbApi.upload(file!, title.trim(), category),
    onSuccess: (doc) => {
      void queryClient.invalidateQueries({ queryKey: ['kb'] });
      close();
      void navigate(`/kb/${doc.id}`);
    },
  });

  const close = () => {
    setFile(null);
    setTitle('');
    setValidation(null);
    upload.reset();
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!file) return setValidation('Choose a file.');
    if (!/\.(md|txt|pdf|docx)$/i.test(file.name)) return setValidation('Supported formats: .md, .txt, .pdf, .docx');
    if (!title.trim()) return setValidation('Enter a title.');
    setValidation(null);
    upload.mutate();
  };

  return (
    <Modal open={open} title="Upload document" onClose={close}>
      <form className="space-y-3" onSubmit={submit}>
        <Field label="File" hint="Markdown, plain text, PDF or Word (.docx). The text is extracted on the server.">
          <input
            type="file"
            accept={ACCEPT}
            className="block w-full text-sm text-ink file:mr-3 file:rounded-md file:border file:border-line file:bg-subtle file:px-3 file:py-1 file:text-sm file:text-ink"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ''));
            }}
          />
        </Field>
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
        </Field>
        <Field label="Category">
          <Select value={category} onChange={(e) => setCategory(e.target.value as KbCategory)}>
            {KbCategorySchema.options.map((c) => (
              <option key={c} value={c}>
                {KB_CATEGORY_LABELS[c]}
              </option>
            ))}
          </Select>
        </Field>
        {validation && <p className="text-xs text-danger">{validation}</p>}
        <ErrorBanner error={upload.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={close}>Cancel</Button>
          <Button type="submit" variant="primary" loading={upload.isPending}>
            Upload
          </Button>
        </div>
      </form>
    </Modal>
  );
}
